import { useState, useEffect, useRef } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, UserCog, Mail, Shield, Loader2, Users, Printer } from "lucide-react";

const MitarbeiterPage = () => {
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });

  // State for print credentials dialog
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const printRef = useRef(null);

  const fetchMitarbeiter = async () => {
    try {
      const res = await api.get("/api/mitarbeiter");
      setMitarbeiter(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Mitarbeiter");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMitarbeiter(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/mitarbeiter", {
        name: formData.name,
        email: formData.email.toLowerCase(),
        password: formData.password,
      });
      toast.success(`${formData.name} wurde als Mitarbeiter angelegt`);
      // Save credentials for print before resetting form
      setCreatedCredentials({
        name: formData.name,
        email: formData.email.toLowerCase(),
        password: formData.password,
      });
      setDialogOpen(false);
      setPrintDialogOpen(true);
      setFormData({ name: "", email: "", password: "" });
      fetchMitarbeiter();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank", "width=700,height=500");
    printWindow.document.write(`
      <html>
        <head>
          <title>Zugangsdaten - ${createdCredentials?.name}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700&family=Inter:wght@400;500;600&display=swap');
            body { font-family: 'Inter', sans-serif; padding: 40px; color: #1a1a1a; }
            .header { text-align: center; margin-bottom: 32px; border-bottom: 3px solid #f97316; padding-bottom: 20px; }
            .header h1 { font-family: 'Barlow Condensed', sans-serif; font-size: 28px; margin: 0 0 4px 0; }
            .header p { color: #666; font-size: 14px; margin: 0; }
            .credentials-box { border: 2px solid #e2e8f0; border-radius: 12px; padding: 24px; margin: 24px 0; background: #f8fafc; }
            .field { margin-bottom: 16px; }
            .field:last-child { margin-bottom: 0; }
            .field-label { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; margin-bottom: 4px; font-weight: 600; }
            .field-value { font-size: 18px; font-weight: 600; color: #0f172a; font-family: monospace; background: white; padding: 8px 12px; border-radius: 6px; border: 1px solid #e2e8f0; }
            .notice { margin-top: 32px; padding: 16px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 8px; font-size: 13px; color: #92400e; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #94a3b8; }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>VyWork - Zugangsdaten</h1>
            <p>Werkstatt-Mitarbeiter Login</p>
          </div>
          <div class="credentials-box">
            <div class="field">
              <div class="field-label">Mitarbeiter</div>
              <div class="field-value">${createdCredentials?.name || ""}</div>
            </div>
            <div class="field">
              <div class="field-label">E-Mail / Benutzername</div>
              <div class="field-value">${createdCredentials?.email || ""}</div>
            </div>
            <div class="field">
              <div class="field-label">Passwort</div>
              <div class="field-value">${createdCredentials?.password || ""}</div>
            </div>
          </div>
          <div class="notice">
            <strong>Wichtig:</strong> Bitte bewahren Sie diese Zugangsdaten sicher auf und geben Sie sie nur dem betreffenden Mitarbeiter. 
            Es wird empfohlen, das Passwort nach dem ersten Login zu &auml;ndern.
          </div>
          <div class="footer">
            Erstellt am ${new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })} | VyWork Auftragsverwaltung
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`${name} wirklich löschen?`)) return;
    try {
      await api.delete(`/api/mitarbeiter/${id}`);
      toast.success(`${name} wurde gelöscht`);
      fetchMitarbeiter();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mitarbeiter-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: "Barlow Condensed" }}>
            Mitarbeiter
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Werkstatt-Mitarbeiter verwalten und Aufträge zuweisen
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2" data-testid="add-mitarbeiter-button">
              <Plus className="w-4 h-4" /> Neuer Mitarbeiter
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Barlow Condensed" }}>Neuen Mitarbeiter anlegen</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="ma-name">Name *</Label>
                <Input
                  id="ma-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Max Mustermann"
                  required
                  data-testid="mitarbeiter-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ma-email">E-Mail *</Label>
                <Input
                  id="ma-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="mitarbeiter@werkstatt.de"
                  required
                  data-testid="mitarbeiter-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ma-password">Passwort * (min. 8 Zeichen)</Label>
                <Input
                  id="ma-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Mindestens 8 Zeichen"
                  required
                  minLength={8}
                  data-testid="mitarbeiter-password-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="btn-primary gap-2" data-testid="mitarbeiter-save-button">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Anlegen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Print Credentials Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="print-credentials-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Barlow Condensed" }} className="text-xl">
              Zugangsdaten erstellt
            </DialogTitle>
          </DialogHeader>
          {createdCredentials && (
            <div ref={printRef} className="space-y-4 mt-2">
              <p className="text-sm text-muted-foreground">
                Der Mitarbeiter wurde erfolgreich angelegt. Sie können die Zugangsdaten jetzt ausdrucken und dem Mitarbeiter überreichen.
              </p>
              <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Mitarbeiter</p>
                  <p className="font-semibold" data-testid="print-cred-name">{createdCredentials.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">E-Mail / Benutzername</p>
                  <p className="font-mono font-semibold" data-testid="print-cred-email">{createdCredentials.email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Passwort</p>
                  <p className="font-mono font-semibold" data-testid="print-cred-password">{createdCredentials.password}</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Hinweis:</strong> Das Passwort wird nur jetzt angezeigt. Bitte drucken Sie es aus oder notieren Sie es, bevor Sie diesen Dialog schließen.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPrintDialogOpen(false)} data-testid="print-cred-close">
              Schließen
            </Button>
            <Button onClick={handlePrint} className="btn-primary gap-2" data-testid="print-cred-button">
              <Printer className="w-4 h-4" />
              Zugangsdaten drucken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {mitarbeiter.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold">Noch keine Mitarbeiter</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-md">
              Legen Sie Mitarbeiter an, damit diese ihre zugewiesenen Aufträge auf dem Tablet oder Handy bearbeiten können.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {mitarbeiter.map((ma) => (
            <Card key={ma.id} data-testid={`mitarbeiter-card-${ma.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserCog className="w-4 h-4 text-primary" />
                    {ma.name}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDelete(ma.id, ma.name)}
                    data-testid={`delete-mitarbeiter-${ma.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="w-3.5 h-3.5" />
                  {ma.email}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    <Shield className="w-3 h-3 mr-1" />
                    Mitarbeiter
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MitarbeiterPage;
